import { type ExamEventData } from '@exam-center/exam-event-management';
import { ApplicantGroupType, RegistrationStatus } from '@exam-center/graphql';
import { dayjs } from '@shared/utils';
import { z } from 'zod';

const EXAM_EVENT_SEAT_MIN_LENGTH = 1;

const APPLICATION_GROUP_CODE_MAX_LENGTH = 256;

export function getExamEventTemporarilyCloseFormSchema(examEvent: ExamEventData) {
  const examEventMinSeat = Math.max(
    examEvent.numberOfExaminees ?? EXAM_EVENT_SEAT_MIN_LENGTH,
    EXAM_EVENT_SEAT_MIN_LENGTH,
  );
  return z
    .object({
      location: z.object({
        province: z.object(
          {
            label: z.string(),
            value: z.string().uuid(),
          },
          {
            message: 'กรุณาเลือกจังหวัด',
          },
        ),
        locationName: z.object(
          {
            label: z.string(),
            value: z.string().uuid(),
          },
          {
            message: 'กรุณาเลือกสถานที่สอบ/หน่วยงาน',
          },
        ),
        room: z
          .record(z.string().uuid(), z.boolean().nullish(), {
            message: 'กรุณาเลือกห้องสอบ',
          })
          .refine(
            (rooms) => {
              const selectedRooms = Object.values(rooms).filter((room) => room);
              return selectedRooms.length > 0;
            },
            {
              message: 'กรุณาเลือกห้องสอบ',
            },
          ),
        seat: z
          .number({ message: 'กรุณากรอกจำนวนที่นั่ง' })
          .min(
            EXAM_EVENT_SEAT_MIN_LENGTH,
            `จำนวนที่นั่งต้องมากกว่า ${EXAM_EVENT_SEAT_MIN_LENGTH - 1}`,
          )
          .refine((data) => data >= examEventMinSeat, {
            message: `จำนวนที่นั่งต้องมากกว่าหรือเท่ากับจำนวนที่ลงทะเบียนแล้ว`,
          }),
      }),
      eventStartDate: z.date({ message: 'ระบุวันที่เปิดรับสมัคร' }),
      eventEndDate: z.date({ message: 'ระบุวันที่ปิดรับสมัคร' }),
      groupType: z
        .object({
          label: z.string(),
          value: z.nativeEnum(ApplicantGroupType),
        })
        .nullish(),
      status: z.nativeEnum(RegistrationStatus),
      groupCode: z
        .string()
        .max(
          APPLICATION_GROUP_CODE_MAX_LENGTH,
          `ความยาวต้องไม่เกิน ${APPLICATION_GROUP_CODE_MAX_LENGTH} ตัวอักษร`,
        )
        .nullish(),
    })
    .refine(
      (data) => {
        const isRegistrationDateChanged =
          !dayjs(examEvent.registrationStartedAt).isSame(data.eventStartDate, 'minute') ||
          !dayjs(examEvent.registrationEndedAt).isSame(data.eventEndDate, 'minute');

        if (!isRegistrationDateChanged) return true;

        const diffInMinutes = dayjs(data.eventEndDate).diff(dayjs().startOf('minute'), 'minute');
        return diffInMinutes >= 15;
      },
      {
        message: 'เวลาปิดรับสมัครต้องมากกว่าเวลาปัจจุบันอย่างน้อย 15 นาที',
        path: ['eventEndDate'],
      },
    )
    .refine(
      (data) => {
        const diffInMinutes = dayjs(examEvent.examStartedAt).diff(
          dayjs(data.eventEndDate),
          'minute',
        );
        return diffInMinutes >= 30;
      },
      {
        message: 'เวลาปิดรับสมัครต้องไม่เกิน 30 นาทีก่อนเริ่มสอบ',
        path: ['eventEndDate'],
      },
    )
    .refine(
      (data) => {
        return data.eventStartDate < data.eventEndDate;
      },
      {
        message: 'เวลาปิดรับสมัครต้องมากกว่าเวลาที่เปิดรับสมัคร',
        path: ['eventEndDate'],
      },
    );
}

export type ExamEventTemporarilyClosedFormType = z.infer<
  ReturnType<typeof getExamEventTemporarilyCloseFormSchema>
>;
